import React, { useState } from 'react';
import { FaQuestionCircle, FaTimes, FaLightbulb } from 'react-icons/fa';
import ReactDOM from 'react-dom';

const GUIDES = {
  'create-itinerary': {
    title: 'Creating Your Itinerary',
    intro: "Welcome to the start of your adventure! This page is where you tell our AI everything it needs to know about your dream trip — and where you'll watch it transform into a fully planned, day-by-day itinerary in under a minute. There are two paths you can take: a fast **Quick Start** flow that suggests trending destinations, or a hands-on **Manual Form** if you already know exactly where you're going.",
    sections: [
      {
        heading: 'Quick Start — let us inspire you',
        items: [
          "**Step 1: Pick a region.** Choose from world regions like Europe, Asia, Americas, Africa, Oceania, or Middle East. These show trending destinations our AI is recommending right now.",
          "**Step 2: Pick a category.** Once you've chosen a region, pick the type of trip — Mountains, Beaches, Cities, Nature, Adventure, Culture, Relaxation, and more. Each category auto-tunes your interests behind the scenes (e.g. Mountains pre-selects Hiking + Nature + Photography).",
          "**Step 3: Pick a destination card.** You'll see real, AI-curated trending spots with photos and descriptions. Tap one and the form below auto-fills with that destination.",
          "Don't see what you want? Use the **Back to regions** link to start over, or skip Quick Start entirely and fill in the form manually.",
        ],
      },
      {
        heading: 'Manual form — every field explained',
        items: [
          "**Start location** — where your journey begins. Type a city, airport code, or address. The autocomplete pulls real geographic data so spelling doesn't have to be perfect.",
          "**End location** — your destination. Same autocomplete. This is locked once the itinerary is created — choose carefully!",
          "**Start & End dates** — pick from the calendar. Your **trip length** calculates automatically and shows below the dates.",
          "**Interests** — pick as many as you want from Hiking, Foodie, Culture, Adventure, Relaxation, Shopping, Nightlife, Spa, Beaches, Photography, Museums, History, Architecture, Nature, Sports, Wildlife, and more. The AI uses these to balance your daily plan — more interests = a more varied itinerary.",
          "**Food preference** — choose from No Restriction, Vegetarian, Vegan, Halal, Kosher, Gluten-Free, or Pescatarian. This filters every restaurant and meal recommendation throughout your trip.",
          "**Transportation mode** — Flying, Driving, Train, Bus, Walking, or Biking. This affects routing logic, distance estimates, and which stops the AI suggests (e.g. driving trips get scenic detours).",
        ],
      },
      {
        heading: 'Driving-specific options',
        items: [
          "**Avoid toll roads** — toggle on if you'd rather take the long way around to save on tolls.",
          "**Prefer toll roads** — toggle on if you want fastest routes even with tolls (defaults vary by region).",
          "**Scenic roads** — toggle on for routing that prioritizes beautiful views over the fastest path. Great for road trips.",
          "These options only appear when transportation is set to Driving.",
        ],
      },
      {
        heading: 'Waypoints — stops along the way',
        items: [
          "Want to break up the journey? Add **waypoints** between your start and end locations.",
          "Each waypoint has its own arrival and departure date, so you can spend multiple days there.",
          "The AI builds out activities at each waypoint based on your interests, just like the main destinations.",
          "Use the **+ Add Waypoint** button — and the **trash icon** to remove one. Drag handles let you reorder.",
          "Perfect for cross-country trips, multi-city European tours, or any \"on the way to X, stop at Y\" plan.",
        ],
      },
      {
        heading: 'Voice input',
        items: [
          "See a **microphone icon** next to a text field? Tap it to dictate instead of typing.",
          "Works with the location, interests, and notes fields — handy when typing isn't convenient.",
          "Browser permissions required for the first use.",
        ],
      },
      {
        heading: 'Edit mode (modifying an existing trip)',
        items: [
          "If you got here from the **Modify** button on an existing itinerary, you're in edit mode.",
          "The destination is **locked** — you can't change start or end locations, only the other settings.",
          "Want a totally different destination? Create a brand new itinerary instead.",
          "Changes regenerate only what needs to change — your custom edits and check-ins stay intact where possible.",
        ],
      },
      {
        heading: 'Generating your itinerary',
        items: [
          "Hit **Generate Itinerary** at the bottom when ready.",
          "You'll jump to a progress screen showing live AI status (researching, mapping, finalizing).",
          "Typical generation time: **30–60 seconds**. Longer trips or destinations with lots of options may take up to 90 seconds.",
          "Once done, you're auto-redirected to your finished itinerary.",
        ],
      },
      {
        heading: 'Subscription limits',
        items: [
          "Free plan: limited number of trips total. Once you hit the limit, you'll see an inline error with an **Upgrade Now** button.",
          "Paid plans (Explorer, Individual, Group, etc.): higher or unlimited trips depending on tier.",
          "Need more trips without upgrading? Look for **Pay-as-you-go** trip credits in Account settings.",
        ],
      },
    ],
  },

  'view-itinerary': {
    title: 'Your Travel Itinerary',
    intro: "Welcome to your trip command center! Everything about your journey lives here — day-by-day plan, route map, flights, packing list, expenses, attractions, group chat, ratings, timeline, and more. This guide walks through every feature so nothing stays hidden.",
    sections: [
      {
        heading: 'Header actions (top of the page)',
        items: [
          "**Modify** — opens the edit form to change dates, interests, transportation, waypoints, and member count. Hidden once your trip has started.",
          "**Translate** — dropdown to switch the entire itinerary into 20+ languages (English, Spanish, French, German, Italian, Portuguese, Japanese, Chinese, Korean, Russian, Arabic, Hindi, Dutch, Polish, Turkish, and more).",
          "**PDF** — generates a beautifully formatted PDF you can download, print, or share with travel companions.",
          "**New Trip** — clears your current view and takes you to start a fresh itinerary.",
          "**Guide (?) button** — opens this very guide. Always available!",
        ],
      },
      {
        heading: 'Travel Advisory Banner',
        items: [
          "Shown at the top when your destination has active travel warnings, weather alerts, or safety notices.",
          "Pulled from official sources and refreshed regularly.",
          "**Dismiss** any advisory you've read — it won't reappear. Use **Refresh** to re-check for new alerts.",
        ],
      },
      {
        heading: 'Tab navigation — every tab explained',
        items: [
          "**Summary** (globe icon) — high-level trip overview: destination summary, key highlights, currency info, weather snapshot, group members. Great for sharing the gist.",
          "**Itinerary** (route icon) — your main day-by-day plan with day cards. The default tab.",
          "**Flights** (plane icon) — manage every flight for this trip (see the Flights guide).",
          "**Checklist** (clipboard icon) — pre-trip to-dos and AI packing list (see the Checklist guide).",
          "**Attractions** (landmark icon) — extra AI-curated picks you can add to any day (see the Attractions guide).",
          "**Expenses** (receipt icon) — track trip spending solo or as a group. Only visible on Group-feature plans.",
          "**Timeline** (map-pin icon) — live activity feed of check-ins and member updates. Only visible during an active trip with invited members.",
          "**Rate Trip** (star icon) — leave star ratings and detailed feedback for the trip overall and individual stops.",
        ],
      },
      {
        heading: 'Day Cards Grid (Itinerary tab)',
        items: [
          "Each card represents one day with title, date, and preview of activities.",
          "**Tap a card** to open the full Day Details view (see the Day Details guide).",
          "Cards show day-level **progress** if you've been ticking off stops.",
          "**Place images** auto-load on each card for visual interest.",
          "Grid view shows all days at once — perfect for a bird's-eye overview.",
        ],
      },
      {
        heading: 'Re-optimize button (Itinerary tab)',
        items: [
          "Found at the top of the Itinerary tab once you've marked some stops as visited.",
          "**Reoptimize** asks the AI to rebuild your remaining itinerary, dropping visited stops and reshuffling the rest for efficiency.",
          "Useful mid-trip when you've already done some things and want the remaining days re-balanced.",
          "**Disabled** until you've marked at least one stop as visited.",
          "**Disabled** once the trip has ended.",
          "Respects **locked** stops — they stay exactly where you put them (see the Day Details guide for locking).",
        ],
      },
      {
        heading: 'Modify form',
        items: [
          "Opens the edit form to change dates, interests, transportation, and waypoints.",
          "AI regenerates only what needs to change — your custom edits, check-ins, locked stops, and ratings are preserved where possible.",
          "**Member count** — adjust the number of travelers (affects suggestions like hotel sizing).",
          "**Hidden once the trip starts** to protect your live plan — but you can still tweak individual days via AI chat or Day Details.",
        ],
      },
      {
        heading: 'AI Chatbot (floating button)',
        items: [
          "Look for the **chat bubble icon** floating on the page.",
          "Ask anything in natural language: \"add a vegetarian dinner on day 3\", \"swap the museum for a beach\", \"add an extra day in Paris\", \"replace the morning hike with shopping\".",
          "The AI reviews your trip, proposes the change, and adds it to a **pending changes** queue.",
          "Review pending changes and **Apply All** or **Discard** individual ones.",
          "Pending changes are queued so you stay in control — nothing changes without your OK.",
        ],
      },
      {
        heading: 'Sharing & collaboration',
        items: [
          "**Share** — generate a link that lets others view your trip. Recipients claim it by signing in.",
          "**Invite as group member** — give people full collaborator access on Group-feature plans. They can edit, check off stops, log expenses, and chat.",
          "**Trip Members** panel shows everyone with access. Owner can remove members.",
          "**Group Chat** — built-in chat for everyone on the trip. Plan meetups, share photos, coordinate in one place.",
        ],
      },
      {
        heading: 'Summary tab features',
        items: [
          "**Trip overview** — start, end, dates, duration, interests, food preference at a glance.",
          "**Currency Card** — live exchange rates for your destination's currency. Tap to convert amounts.",
          "**Weather snapshot** — current and upcoming conditions for the destination.",
          "**Group members** — list of who's on the trip with you.",
          "**Internal links** — quick jumps to related guides, blog articles, and tools.",
        ],
      },
      {
        heading: 'Timeline tab features',
        items: [
          "Only visible when there are **invited group members** AND the trip is **currently active**.",
          "Live feed of check-ins, ratings, photos, and member activity as the trip unfolds.",
          "Great for staying connected when you've split up during the day.",
        ],
      },
      {
        heading: 'Rate Trip tab',
        items: [
          "**Overall trip rating** — leave a star rating once your trip is done.",
          "**Per-stop ratings** — give stars to individual attractions, restaurants, hotels.",
          "**Written feedback** — share what you loved or what could be better.",
          "Helps the AI improve and learn what you actually enjoy.",
        ],
      },
      {
        heading: 'Other handy features',
        items: [
          "**Review Links** — quick links to read or write reviews of places you've visited on Google/TripAdvisor.",
          "**Auto-save** — every change is saved instantly. Close the tab and come back — it's all here.",
          "**Offline mode** (premium tiers) — pre-loads your itinerary so it works without internet during travel.",
          "**Rating prompt modal** — gentle reminder to rate after key trip moments.",
        ],
      },
      {
        heading: 'After your trip ends',
        items: [
          "Reoptimize and Modify buttons disable.",
          "Check-ins, ratings, and photos stay accessible.",
          "You'll see a **Rate Your Trip** prompt — leave overall and per-stop feedback.",
          "Itinerary stays available forever so you can revisit, share with others, or use as a template.",
        ],
      },
    ],
  },

  'view-day': {
    title: 'Day Details',
    intro: "This is where you live during your trip — the play-by-play of a single day. Every stop, every meal, every recommended activity in the right order, with maps, directions, and powerful per-stop actions to keep your day exactly the way you want it.",
    sections: [
      {
        heading: 'Header info at a glance',
        items: [
          "**Back arrow** (top-left) — close the day view and return to the trip overview.",
          "**Close (X)** (top-right) — same as Back; closes the slide-over.",
          "**Guide (?)** button — opens this guide.",
          "**Day badge** (e.g. \"Day 3\") + the **actual date** in your trip.",
          "**Location name** — the city or region you're based in that day.",
          "**Day title** — a quick descriptive label (\"Old Town walking tour\", \"Beach day\").",
          "**Progress bar** — visualizes how many stops you've checked off vs. total stops.",
          "**Completion count** (e.g. \"4/7 completed\") shows how the day is going.",
          "**Read Day** button — narrates the entire day's plan aloud (text-to-speech).",
          "**Weather card** — emoji + temperature for that day's forecast.",
        ],
      },
      {
        heading: 'Top Quick Action — Full Day Route',
        items: [
          "**Open Full Route in Google Maps** button (top of stops list) — opens Google Maps with EVERY stop chained together as a single multi-waypoint route.",
          "Use this when starting the day so your phone can navigate the entire day's plan.",
          "Defaults to driving, but you can change the travel mode inside Maps.",
        ],
      },
      {
        heading: 'Each stop card shows',
        items: [
          "**Place image** — high-res photo (or a placeholder if none exists).",
          "**Type icon** — utensils icon for meals, bed icon for accommodations.",
          "**Stop name** — strikethrough when you've marked it visited.",
          "**Star rating** — shown when public ratings are available.",
          "**Coordinates** (lat/lng) — handy for offline maps or sharing exact location.",
          "**Description** — a brief overview (up to 3 lines, truncated for long ones).",
          "**Custom time** — if you've set a specific time for the stop, it shows with a clock icon.",
          "**Hotel booking options** — for accommodation stops with check-in dates pre-filled.",
        ],
      },
      {
        heading: 'Card color tells you the status',
        items: [
          "**White card** = a stop you haven't visited yet.",
          "**Green card** = a stop you've marked as visited.",
          "**Amber card** = a stop you've **locked** (see Lock feature below).",
          "**Hover/tap effect** — cards subtly highlight in sky blue when you hover over them.",
        ],
      },
      {
        heading: 'Mark a stop as visited (circle icon)',
        items: [
          "Tap the **empty circle** next to any stop to mark it visited.",
          "Visited stops turn green with a strikethrough name and a green checkmark.",
          "Counts toward your day progress bar at the top.",
          "Tap the green checkmark again to undo (mark unvisited).",
          "Group trips: everyone's check-ins sync in real-time so the whole crew stays aligned.",
        ],
      },
      {
        heading: 'Lock / Unlock a stop (owner only)',
        items: [
          "**Lock icon** next to the visited toggle — tap to lock a stop in place.",
          "**Locked stops are protected from AI re-optimization** — if you run **Reoptimize** (in Attractions tab) or ask the AI chat to reshuffle, locked stops stay exactly where they are.",
          "Use this for **non-negotiable plans**: a restaurant reservation at 7pm, a flight you can't miss, a hotel check-in time, a paid tour with a fixed start.",
          "Locked stops show with an **amber card background** so they're visually obvious.",
          "Tap the lock again to unlock — the stop becomes eligible for re-optimization again.",
          "**Only the trip owner** sees this button. Group members can mark visited but can't lock.",
        ],
      },
      {
        heading: 'Per-stop action row (bottom of each card)',
        items: [
          "**More Info** (indigo) — opens a Google search/Wikipedia-style info page for the stop in a new tab.",
          "**View on Map** (sky blue) — opens the stop's exact location in Google Maps.",
          "**Directions** (teal) — opens Google Maps with the stop as the destination, ready to navigate from your current location.",
          "**Reviews** — quick link to read what others have said (Google reviews, TripAdvisor, etc.) about that specific spot.",
          "**Remove** (red, owner only) — drops the stop from your itinerary (queued, see below).",
        ],
      },
      {
        heading: 'Remove a stop (owner only)',
        items: [
          "Tap **Remove** on a stop you don't want.",
          "It enters a **queued state** — the card shows an amber \"Queued for removal\" banner.",
          "**Apply pending changes** in the AI Chat panel to commit the removal — your itinerary then regenerates around the gap.",
          "If you change your mind before applying, the removal stays queued until you do.",
          "Group members can't remove stops — only the owner.",
        ],
      },
      {
        heading: 'Explore stops along the way',
        items: [
          "Between two consecutive stops, look for the **Explore stops along the way to [next stop]** button (emerald/teal banner).",
          "Tap it to open a discovery modal of nearby attractions on the route between those two points.",
          "Add any of them to your day — the route auto-recalculates.",
          "Perfect when you're realizing you have time to spare between two planned stops.",
        ],
      },
      {
        heading: 'Read Aloud (text-to-speech)',
        items: [
          "**Read Day** button (in the header) — narrates the entire day's plan from start to finish.",
          "Tap **Stop** to interrupt narration anytime.",
          "Perfect while driving, walking, or when you want a hands-free briefing of what's planned.",
          "Voice and speed are customizable in **Account → Voice Settings**.",
          "Supports many languages — matches the language you've set for your itinerary.",
        ],
      },
      {
        heading: 'Day Weather',
        items: [
          "Forecast for that specific day appears in the header — temperature high (°F) + an emoji for conditions (☀️ ☁️ 🌧️ ❄️).",
          "Updates daily, available up to ~2 weeks before the trip.",
          "Helps you decide outfits and whether to swap outdoor stops for indoor ones if weather turns.",
          "If weather data isn't available for that date, the card simply doesn't appear.",
        ],
      },
      {
        heading: 'Tips & navigation',
        items: [
          "Day Details opens as a **slide-over panel** so the main itinerary stays in context underneath.",
          "On mobile, it becomes a full-screen view for max readability.",
          "Press **Esc** to close on web.",
          "Tap the dimmed area outside the panel to close.",
          "Day view auto-saves all changes (visits, locks, removals) — no Save button needed.",
        ],
      },
    ],
  },

  'flights': {
    title: 'Flights',
    intro: "One-stop tracking for every flight on your trip. Add bookings, get real-time status updates, scan paper tickets, and let your itinerary intelligently adjust around your actual flight times.",
    sections: [
      {
        heading: 'Three ways to add a flight',
        items: [
          "**Manual entry** — pick the airline, type the flight number and date. We'll auto-fetch the route, times, and terminals from live aviation data.",
          "**Flight lookup** — just type a flight number and date; we look it up and pre-fill everything for you. Less typing!",
          "**Scan ticket** (OCR) — tap the camera icon and upload a photo of your paper ticket or boarding pass. Our AI extracts the flight info automatically.",
          "**Upload ticket** — attach a PDF or image of your e-ticket to keep it with the flight record.",
        ],
      },
      {
        heading: 'Outbound vs. Return flights',
        items: [
          "**Outbound** — flights heading TO your destination (or onward legs during the trip).",
          "**Return** — flights heading home. They're grouped separately so you can find them fast.",
          "Each section can hold multiple flights — useful for multi-city trips or open-jaw bookings.",
        ],
      },
      {
        heading: 'Connecting / multi-leg flights',
        items: [
          "Tap **+ Add Connecting Flight** on any existing flight to chain a connection.",
          "Each leg shows its own departure/arrival, terminal, and layover duration.",
          "Layover warnings appear if there's tight timing (e.g. \"40 min layover — could be tight\").",
        ],
      },
      {
        heading: 'Real-time status',
        items: [
          "**On-time, delayed, boarding, departed, landed, cancelled** — auto-updated from live aviation data.",
          "**Gate changes** show up as soon as airlines publish them.",
          "Status badge appears on every flight card.",
          "**Refresh** button forces a manual status check anytime.",
        ],
      },
      {
        heading: 'Itinerary sync with flight times',
        items: [
          "After adding flights, click **Regenerate Itinerary with Flight Times**.",
          "The AI rebuilds Day 1 around your real landing time (no more 9am museum stops when you land at 3pm!).",
          "Same for the last day — your departure flight time becomes the cut-off for the final day's activities.",
          "Run regenerate anytime your flight changes (delayed, rebooked).",
        ],
      },
      {
        heading: 'Editing & deleting',
        items: [
          "**Edit (pencil icon)** — fix a typo, change the date, update terminal info.",
          "**Delete (trash icon)** — remove a flight you no longer have. Confirmation dialog prevents accidents.",
          "Only the trip owner (or group members on collaborative plans) can edit flights.",
        ],
      },
      {
        heading: 'Airport search',
        items: [
          "When entering airports manually, start typing the name or IATA code (e.g. \"LHR\", \"JFK\", \"Heathrow\").",
          "Autocomplete shows airport name, code, city, and country.",
          "Useful for non-major airports the AI might not pre-populate.",
        ],
      },
      {
        heading: 'Tips & best practices',
        items: [
          "Add flights **before** you generate your itinerary — Day 1 will be smarter from the start.",
          "Flights are **optional** — perfectly fine to skip if you're driving or taking trains.",
          "Multi-passenger? Add one flight per booking confirmation, not per passenger.",
          "Tickets you upload are stored securely and only visible to trip members.",
        ],
      },
    ],
  },

  'checklist': {
    title: 'Travel Checklist & Packing',
    intro: "Two tools, one tab. The **Travel Checklist** at the top is for everything you need to handle before you leave (passport, bookings, home prep). The **Packing List** at the bottom is an AI-generated list of everything to actually throw in your bag, tailored to your destination, weather, and trip length.",
    sections: [
      {
        heading: 'Travel Checklist — pre-built sections',
        items: [
          "**Documents** — passport, visa, driver's licence, travel insurance.",
          "**Bookings** — confirm flights, hotels, rental cars, activities.",
          "**Finances** — notify bank, get cash in destination currency, set up cards.",
          "**Health** — vaccines, prescriptions, first-aid, travel insurance.",
          "**Home prep** — stop mail, water plants, set thermostat, ask neighbor.",
          "Each section is collapsible — tap to expand or collapse.",
          "**Tick items off** as you complete them. Sections turn green when fully done.",
        ],
      },
      {
        heading: 'Custom checklist actions',
        items: [
          "**+ Add Section** (header) — create your own custom section (e.g. \"Pet sitter\", \"Work handover\", \"Kids' school\").",
          "**+ Add Item** to any section — including custom items inside pre-built sections.",
          "**Edit (pencil icon)** — change the wording of any item, both pre-built and custom.",
          "**Remove (trash icon)** — delete an item or an entire custom section.",
          "All changes save automatically — no save button needed.",
        ],
      },
      {
        heading: 'AI Packing List — getting started',
        items: [
          "Tap **Generate Packing List** to have AI build a full list from scratch.",
          "Considers your destination's **climate**, the **time of year**, **trip length**, and your **interests** (e.g. hiking gear for Hiking, snorkel mask for Beaches).",
          "Items are grouped by category: Clothing, Toiletries, Electronics, Documents, Health/Medicine, Activity Gear, and more.",
          "Each item has a **quantity** and a **brief note** (e.g. \"3 pairs — quick-dry preferred\").",
          "First-time generation takes ~10-20 seconds while AI tailors the list.",
        ],
      },
      {
        heading: 'Importance tags (color-coded)',
        items: [
          "**Essential** (red) — must-haves like passport, medication, chargers.",
          "**Recommended** (yellow) — strongly suggested but not life-or-death (sunglasses, reusable water bottle).",
          "**Optional** (gray) — nice-to-haves (extra outfit, travel pillow).",
          "Use tags to prioritize when packing space is tight.",
        ],
      },
      {
        heading: 'Working with packing items',
        items: [
          "**Check items off** as you pack — the counter at the top tracks `total checked / total items`.",
          "**Edit (pencil)** — adjust name or quantity.",
          "**Delete (trash)** — remove an item; confirmation dialog prevents accidents.",
          "**Move item** — relocate an item to a different category using the move icon. Useful when AI categorizes things differently from how you'd organize them.",
          "**Move to Uncategorized** — relocate an item out of categories entirely.",
        ],
      },
      {
        heading: 'Categories — manage them',
        items: [
          "Categories are **collapsible** — tap the category header to expand/collapse.",
          "**+ Add Item** inside any category.",
          "**Delete category** (trash on the category header) — confirms before deleting; items inside are also removed.",
          "**+ Category** in the header — create a brand new category for things AI didn't think of (\"Photography gear\", \"Baby supplies\", \"Climbing equipment\").",
          "**Uncategorized** items sit in their own bucket at the bottom — handy for one-off odds and ends.",
        ],
      },
      {
        heading: 'Re-suggest, Refresh, Regenerate',
        items: [
          "**Re-suggest** (magic wand button) — AI reviews what's still unchecked and adds anything you might've missed. Great mid-trip if your needs have shifted.",
          "**Refresh** (sync icon) — pulls the latest packing list state from the server (useful for group trips).",
          "**Regenerate from scratch** — delete everything, then tap **Generate Packing List** again. AI re-runs with potentially-updated trip details.",
        ],
      },
      {
        heading: 'Tips for packing pros',
        items: [
          "Check the **weather forecast** in your trip overview before packing — last-minute changes happen.",
          "Pack **essentials in carry-on** (passport, meds, chargers) even if you're checking a bag.",
          "Group trip? Each member has their own packing list — no shared confusion.",
          "Pack early, check the list later, and adjust as you go.",
          "Use **Re-suggest** before leaving for any last items the AI thinks you missed.",
        ],
      },
    ],
  },

  'attractions': {
    title: 'Attractions',
    intro: "Your AI-curated discovery hub. Beyond the stops already in your itinerary, this tab surfaces extra attractions matching your interests — perfect for filling unexpected free time, replacing stops you don't love, or just exploring what your destination has to offer.",
    sections: [
      {
        heading: 'How the AI picks them',
        items: [
          "Every attraction is **filtered by your trip's interests** (foodie, nature, history, etc.).",
          "Location-aware — all reachable during your trip without big detours.",
          "Mix of **must-sees** (iconic landmarks) and **hidden gems** (local favorites you wouldn't find in a guidebook).",
          "Refreshed each time you reopen the trip, so new ideas keep flowing.",
        ],
      },
      {
        heading: 'Each attraction card shows',
        items: [
          "**Hero photo** — tap to expand into a full-screen gallery view.",
          "**Name & description** — quick summary of what makes it special.",
          "**Star rating** — when public ratings are available.",
          "**Category badge** — Restaurant, Museum, Landmark, Park, etc.",
          "**Bookable badge** (green) — direct tickets or tours available.",
          "**Expand button** — reveal more details, additional photos, and metadata.",
        ],
      },
      {
        heading: 'Add to your itinerary',
        items: [
          "**Add to Day** — pick which day to slot the attraction into. The day's route auto-recalculates.",
          "**Replace stop** — swap an existing stop in your itinerary with this attraction.",
          "When you Replace, you pick **which day and which stop** to swap out — the rest of the day stays put.",
          "**Loading state** shows briefly while the attraction is added; you'll see it appear in the day card.",
          "Newly added attractions appear on the route map immediately so you can see how they fit.",
        ],
      },
      {
        heading: 'Free plan limits & upgrade',
        items: [
          "Free users see a **counter banner**: \"Free plan: X/Y attractions added\".",
          "Reach the limit and an **amber banner with Upgrade Plan button** appears.",
          "Paid plans have **unlimited adds**, plus access to more curated picks.",
          "Tap **Upgrade Plan** to jump directly to pricing.",
        ],
      },
      {
        heading: 'Booking options',
        items: [
          "Direct booking links (GetYourGuide, Viator, official sites) on attractions that offer tickets/tours.",
          "Click through to reserve without leaving the app.",
          "Opens in a new tab so you don't lose your trip context.",
          "Booked something? Log it as a quick **expense** in the Expenses tab to track spending.",
        ],
      },
      {
        heading: 'Photo gallery',
        items: [
          "Tap any photo to open a full-screen lightbox.",
          "Swipe (mobile) or arrow keys (web) to flip through multiple photos.",
          "Tap outside the image or press Esc to close.",
          "Great for previewing a place before committing it to your itinerary.",
        ],
      },
      {
        heading: 'Tips',
        items: [
          "Add attractions **early** while planning so they slot naturally into the route.",
          "Stuck on what to do with a free morning? This tab is your go-to.",
          "Use **Replace** when you find something better than what's already planned.",
          "Note: **Re-optimize** is on the **Itinerary tab**, not here — that's where you reshuffle your day-by-day plan.",
        ],
      },
    ],
  },

  'expenses': {
    title: 'Trip Expenses',
    intro: "Track every penny you spend — solo or as a group — and split costs fairly without awkward end-of-trip math. Multi-currency, AI receipt scanning, and PDF/CSV export all built in.",
    sections: [
      {
        heading: 'Header at a glance',
        items: [
          "**Expense count** — shows total number of expenses (\"3 expenses\").",
          "**Running total** — sum of all expenses, displayed in your primary trip currency.",
          "**CSV export** — download a clean spreadsheet of every expense.",
          "**PDF export** — generate a formatted printable report.",
          "**Refresh** — re-pull the latest expenses (handy on group trips where others are adding entries).",
          "**+ Add Expense** — open the form to log a new expense.",
          "**Guide (?)** — opens this guide.",
        ],
      },
      {
        heading: 'Adding an expense — fields explained',
        items: [
          "**Place name** — where you spent (e.g. \"Trattoria da Mario\", \"Train ticket\", \"Hotel Astoria\"). Required.",
          "**Amount** — the cost. Required.",
          "**Currency** — pick from a global currency dropdown (USD, EUR, JPY, GBP, INR, AUD, CAD, and many more).",
          "**Date & time** — defaults to right now; change for past expenses.",
          "**Description** — optional notes (\"Group dinner, 4 ppl\", \"Day 3 lunch\").",
          "**Paid by** — defaults to you. On group trips, pick any active member who actually paid.",
        ],
      },
      {
        heading: 'Receipt capture (AI-powered)',
        items: [
          "**Take Photo** — snap a quick photo of your receipt with your camera (mobile).",
          "**Upload Image** — pick a photo or PDF from your library.",
          "**AI receipt scanning** runs automatically and extracts place name, amount, currency, date, and description.",
          "Review the auto-filled form, tweak anything that looks off, then save.",
          "Receipt images are attached to the expense as proof — useful for reimbursement or warranty claims.",
        ],
      },
      {
        heading: 'Multi-currency made easy',
        items: [
          "Enter expenses in **any currency** — we use live exchange rates to convert to your trip's primary currency.",
          "**Total at the top** is always shown in primary currency, no matter which mix of currencies your expenses use.",
          "Each individual expense displays in its **original currency** so you remember what you actually paid.",
          "Primary currency is set automatically based on your destination but can be changed.",
        ],
      },
      {
        heading: 'Group splitting & settlements',
        items: [
          "**Paid by** field — record who actually paid for each expense.",
          "**Settlement Summary** appears automatically at the bottom of the expense list once group expenses exist.",
          "Shows **who owes whom** and **how much** in your primary currency.",
          "Uses a **minimum-payments algorithm** — suggests the simplest set of transactions to settle up (no need for every person to pay every other person).",
          "Updates **live** as expenses are added, edited, or deleted.",
          "Each group member sees the same expense list in real-time.",
        ],
      },
      {
        heading: 'Editing & deleting',
        items: [
          "Each expense card has **Edit (pencil)** and **Delete (trash)** icons.",
          "**Edit** — fix typos, change amounts or currency, swap who paid, update the date.",
          "**Delete** — opens a confirmation dialog to prevent accidents.",
          "List, total, and settlement summary recalculate immediately after any change.",
        ],
      },
      {
        heading: 'Export — CSV',
        items: [
          "Tap **CSV** in the header.",
          "Includes: date, place name, amount, currency, paid-by name, and description for every expense.",
          "Final row shows the **converted total** in your primary currency.",
          "Opens in Excel, Google Sheets, Numbers, or any spreadsheet tool.",
          "Disabled when there are no expenses to export.",
        ],
      },
      {
        heading: 'Export — PDF',
        items: [
          "Tap **PDF** in the header.",
          "Generates a printable report with trip overview, full expense list, and the settlement summary.",
          "Great for sharing with the group at end-of-trip, filing with an accountant, or reimbursement.",
          "Disabled when there are no expenses to export.",
        ],
      },
      {
        heading: 'Plan tier requirements',
        items: [
          "**Solo expense tracking** — available on most paid plans.",
          "**Group splitting & settlements** — requires Group-feature plan (Group, Starter Agent, Professional, Enterprise, Unlimited).",
          "**Free plan** — limited number of expenses per trip. When you hit the limit, the **+ Add Expense** button redirects you to the upgrade page.",
          "If Expenses tab isn't visible at all, the trip owner's plan doesn't include group expense features.",
        ],
      },
      {
        heading: 'Pro tips',
        items: [
          "**Log expenses as you spend them** — way easier than trying to reconstruct at the end of the trip.",
          "Use **AI receipt scanning** for dinner bills — auto-splits the math.",
          "Mark **lodging** as shared early — usually the biggest single cost.",
          "Export the **PDF** at the end of the trip and send to everyone — settling up made simple.",
          "Add the **receipt photo** to every expense to have proof if anyone questions it.",
        ],
      },
    ],
  },
};

// Render **bold** markdown inline
const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-neutral-900">{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const GuideModal = ({ pageKey, onClose }) => {
  const guide = GUIDES[pageKey];

  if (!guide) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="page-guide-title"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <FaLightbulb className="text-white text-xl" />
            <h2 id="page-guide-title" className="text-xl font-bold text-white">{guide.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close guide"
          >
            <FaTimes className="text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <p className="text-neutral-700 text-base leading-relaxed mb-6">
            {guide.intro}
          </p>

          {guide.sections.map((section, idx) => (
            <div key={idx} className="mb-6 last:mb-2">
              <h3 className="text-base font-bold text-primary-700 mb-3 flex items-center gap-2">
                <span className="inline-block w-1.5 h-5 bg-primary-500 rounded-full" />
                {section.heading}
              </h3>
              <ul className="space-y-2.5">
                {section.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-neutral-700 text-sm leading-relaxed">
                    <span className="text-primary-500 mt-1 flex-shrink-0">•</span>
                    <span>{renderInline(item)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-6 flex items-start gap-3">
            <FaLightbulb className="text-amber-500 text-base flex-shrink-0 mt-0.5" />
            <p className="text-amber-800 text-sm">
              <strong>Stuck on something?</strong> Tap the chat icon anywhere in the app to ask our AI — it knows everything about your trip!
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-neutral-200 flex-shrink-0 bg-neutral-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-5 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/**
 * PageGuide — small "?" help button that opens a page-specific guide modal.
 *
 * Usage:
 *   <PageGuide pageKey="create-itinerary" />
 *   <PageGuide pageKey="flights" variant="light" />
 *
 * Variants:
 *   - 'default' — dark icon on light background (use on white/light headers)
 *   - 'light'   — light icon on dark background (use on gradient/colored headers)
 */
const PageGuide = ({ pageKey, variant = 'default', className = '' }) => {
  const [open, setOpen] = useState(false);

  const baseStyles = 'inline-flex items-center justify-center gap-2 rounded-full font-medium transition-all';
  const variantStyles =
    variant === 'light'
      ? 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 px-3 py-2'
      : 'bg-primary-50 text-primary-600 hover:bg-primary-100 px-3 py-2';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`${baseStyles} ${variantStyles} ${className}`}
        aria-label="Open page guide"
        title="Need help? Open guide"
      >
        <FaQuestionCircle className="text-base" />
        <span className="text-sm hidden sm:inline">Guide</span>
      </button>
      {open && <GuideModal pageKey={pageKey} onClose={() => setOpen(false)} />}
    </>
  );
};

export default PageGuide;
